import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import { Box, Button, SxProps, TextField, Theme, Tooltip, useTheme } from '@mui/material';
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NumberFormatValues, NumericFormat } from 'react-number-format';

import { ReactComponent as DeleteIcon } from '../../../assets/images/delete-icon.svg';
import { BUTTON_BUTTON } from '../../../types/forms';

interface IProps {
  value?: number;
  minValue?: number;
  maxValue?: number;
  onChange?: (value?: number) => void;
  onDelete?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  children?: ReactElement;
  sx?: SxProps<Theme>;
  deleteDisabledTooltip?: string;
}

export const QuantitySelector: React.FC<IProps> = ({
  onChange,
  onDelete,
  disabled,
  value = 0,
  minValue = 1,
  maxValue = 9999,
  readOnly,
  children,
  sx,
  deleteDisabledTooltip,
}) => {
  const { palette, sass } = useTheme();
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const increase = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();

      if (typeof onChange === 'function') {
        onChange(value + 1);
      }
    },
    [onChange, value],
  );

  const decrease = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();

      // If value is 1 and onDelete is not provided, do nothing (prevent decreasing to 0)
      if (value === 1 && !onDelete) {
        return;
      }

      // If the value is 1, show 0 and schedule deletion after 1 second
      if (value === 1 && onDelete) {
        // Clear any existing delete timeout
        if (deleteTimeoutRef.current) {
          clearTimeout(deleteTimeoutRef.current);
          deleteTimeoutRef.current = null;
        }

        // Set deleting state to show 0 in the input
        setIsDeleting(true);

        // Schedule deletion after 1 second
        deleteTimeoutRef.current = setTimeout(() => {
          onDelete();
          setIsDeleting(false);
          deleteTimeoutRef.current = null;
        }, 1000);
        return;
      }

      if (typeof onChange === 'function') {
        onChange(value - 1);
      }
    },
    [onChange, value, onDelete],
  );

  const handleInputChange = useCallback(
    (v: NumberFormatValues) => {
      const { floatValue } = v || {};

      // Clear any existing delete timeout
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
        deleteTimeoutRef.current = null;
      }

      // If user manually enters 0 and onDelete is not provided, prevent it
      if (floatValue === 0 && !onDelete) {
        return;
      }

      // If user manually enters 0, schedule deletion after 1 second
      if (floatValue === 0 && onDelete) {
        deleteTimeoutRef.current = setTimeout(() => {
          onDelete();
          deleteTimeoutRef.current = null;
        }, 1000);
        return;
      }

      onChange?.(floatValue);
    },
    [onChange, onDelete],
  );

  const handleBlur = useCallback(() => {
    // Clear any pending delete timeout on blur
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }

    // If value is 0 and onDelete is provided, remove the item immediately on blur
    if (value === 0 && onDelete) {
      onDelete();
      return;
    }

    // If value is 0 and onDelete is not provided, reset to minValue
    if (value === 0 && !onDelete) {
      onChange?.(minValue);
      return;
    }

    if (typeof minValue !== 'undefined' && value < minValue) {
      onChange?.(minValue);
    }
  }, [minValue, value, onChange, onDelete]);

  const buttonStyles = useMemo(
    () => ({
      width: 24,
      height: 24,
      borderRadius: 0.5,
      p: 0,
      minWidth: 24,
    }),
    [],
  );

  const displayValue = useMemo(() => {
    return isDeleting ? 0 : value;
  }, [isDeleting, value]);

  const isBtnDisabled = useMemo(() => disabled || isDeleting, [disabled, isDeleting]);

  const isDecreaseBtnDisabled = useMemo(() => {
    // Disable decrease button when value is 1 and onDelete is not provided
    return isBtnDisabled || (value === 1 && !onDelete);
  }, [isBtnDisabled, value, onDelete]);

  const isIncreaseBtnDisabled = useMemo(() => value >= maxValue || isBtnDisabled, [value, maxValue, isBtnDisabled]);

  const showDeleteTooltip = useMemo(() => {
    return value === 1 && !onDelete && deleteDisabledTooltip;
  }, [value, onDelete, deleteDisabledTooltip]);

  const isIncorrectValue = useMemo(() => {
    return value < minValue || value > maxValue;
  }, [value, minValue, maxValue]);

  const inputBorderStyle = useMemo(() => {
    switch (true) {
      case isIncorrectValue:
        return `1px solid red !important`;
      case disabled:
        return `1px solid ${palette.action.focus} !important`;
      default:
        return `1px solid ${palette.divider} !important`;
    }
  }, [disabled, isIncorrectValue, palette]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box sx={{ display: 'flex', gap: 1, position: 'relative', ...sx }}>
      <Tooltip title={showDeleteTooltip ? deleteDisabledTooltip : ''} placement="top" arrow>
        <span>
          <Button
            onClick={decrease}
            type={BUTTON_BUTTON}
            variant="outlined"
            color="primary"
            size="small"
            sx={buttonStyles}
            disabled={isDecreaseBtnDisabled}
            data-action={value === 1 ? 'quantity_selector_delete' : 'quantity_selector_decrease'}
          >
            {value === 1 ? (
              <Box
                sx={{
                  '& svg': { fill: isDecreaseBtnDisabled ? palette.action.disabled : 'currentColor' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DeleteIcon />
              </Box>
            ) : (
              <RemoveIcon />
            )}
          </Button>
        </span>
      </Tooltip>
      <NumericFormat
        value={displayValue}
        allowNegative={false}
        valueIsNumericString
        allowLeadingZeros={false}
        onBlur={handleBlur}
        readOnly={readOnly || isDeleting}
        decimalScale={0}
        onValueChange={handleInputChange}
        maxLength={maxValue.toString().length}
        disabled={disabled}
        customInput={TextField}
        onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
        slotProps={{
          input: {
            sx: {
              display: 'flex',
              width: 49,
              height: 24,
              borderRadius: 0.5,
              '& fieldset': {
                border: inputBorderStyle,
              },
              '& input': {
                p: 0,
                textAlign: 'center',
                WebkitTextFillColor: disabled
                  ? `${palette.text.disabled} !important`
                  : `${palette.text.primary} !important`,
              },
            },
          },
        }}
        data-action="quantity_selector_input"
      />
      <Button
        type={BUTTON_BUTTON}
        variant="contained"
        color="primary"
        size="small"
        sx={buttonStyles}
        onClick={increase}
        disabled={isIncreaseBtnDisabled}
        data-action="quantity_selector_increase"
      >
        <AddIcon sx={{ color: sass.white }} />
      </Button>
      {children}
    </Box>
  );
};
